var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import Axios from 'axios';
import { ContainerClient } from '@azure/storage-blob';
import { v4 as uuid } from 'uuid';
/**
 * Pulls a SAS/presigned upload URL out of an Axios GET /upload-url response.
 *
 * The shared Axios response interceptor installed below catches failed requests
 * and resolves with a bare `{ error: { message, statusCode, originalError } }` shape
 * instead of rejecting. Callers that were written as `.then(res => res.data.url)`
 * therefore crashed with `Cannot read properties of undefined (reading 'url')`
 * (see ImageBlock upload flow). This helper centralizes the handling so the real
 * backend error message is surfaced to the user instead.
 */
function extractUploadUrl(res, label) {
    var _a, _b, _c;
    if (res === null || res === void 0 ? void 0 : res.error) {
        const err = res.error;
        throw new Error((err === null || err === void 0 ? void 0 : err.message) || `Failed to get upload URL from ${label}`);
    }
    const url = (_a = res === null || res === void 0 ? void 0 : res.data) === null || _a === void 0 ? void 0 : _a.url;
    if (!url || typeof url !== 'string') {
        if ((_c = (_b = res === null || res === void 0 ? void 0 : res.data) === null || _b === void 0 ? void 0 : _b.error) === null || _c === void 0 ? void 0 : _c.message) {
            throw new Error(res.data.error.message);
        }
        throw new Error(`Upload URL response from ${label} was empty`);
    }
    return url;
}
/** PUT a file to a pre-signed URL, reporting progress. Rejects on non-2xx or network error. */
function putWithProgress(url, file, onProgress) {
    return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('PUT', url);
        xhr.setRequestHeader('Content-Type', file.type);
        if (onProgress) {
            xhr.upload.onprogress = (ev) => onProgress(ev.loaded, ev.lengthComputable ? ev.total : file.size);
        }
        xhr.onload = () => {
            if (xhr.status >= 200 && xhr.status < 300)
                resolve();
            else
                reject(new Error(`Storage upload failed (${xhr.status} ${xhr.statusText})`));
        };
        xhr.onerror = () => reject(new Error('Storage upload failed (network error)'));
        xhr.onabort = () => reject(new Error('Storage upload aborted'));
        xhr.send(file);
    });
}
class BackendAPIClient {
    constructor(endpointUrl, authToken, site) {
        this.Media = {
            path: '/media',
            getPublicUrl: (filename, expireyDays = 1, download = false, fileType = 'data', requestId) => __awaiter(this, void 0, void 0, function* () {
                var _a;
                const reqUrl = new URL(this.Media.path + `/${encodeURIComponent(filename)}`, Axios.defaults.baseURL);
                reqUrl.searchParams.append('download', download.toString());
                reqUrl.searchParams.append('expiryDays', expireyDays.toString());
                const result = yield Axios.get(reqUrl.toString(), { headers: { 'X-File-Type': fileType, 'X-Request-Id': requestId !== null && requestId !== void 0 ? requestId : '' } });
                // The shared Axios error interceptor resolves failed requests with a bare `{ error }` shape
                // (no `.data`). Guard against that so we don't throw "Cannot read properties of undefined".
                if (result === null || result === void 0 ? void 0 : result.error) {
                    console.error("Error getting public url for media file", result.error);
                    return { publicUrl: '' };
                }
                if ((_a = result === null || result === void 0 ? void 0 : result.data) === null || _a === void 0 ? void 0 : _a.error) {
                    console.error("Error getting public url for media file", result.data.error);
                    return { publicUrl: '' };
                }
                return result.data;
            }),
            /**
             * Uploads a file straight to storage from the browser.
             * `onProgress` (optional) is called with (loadedBytes, totalBytes) as the upload advances.
             */
            uploadFile: (file, fileType, onProgress) => __awaiter(this, void 0, void 0, function* () {
                var _b;
                const apiSite = String(((_b = Axios.defaults.headers.common) === null || _b === void 0 ? void 0 : _b['source']) || this.site || '').toLowerCase();
                const cbcFileType = (fileType || 'custom_attachment');
                if (apiSite === 'cbc') {
                    // Sanitize the filename by removing/replacing problematic characters
                    const sanitizedName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
                    let fileName = `${Date.now()}_${sanitizedName}`;
                    // DB schema limits file name to 45 characters
                    if (fileName.length > 45) {
                        const ext = sanitizedName.split('.').pop();
                        fileName = fileName.substring(0, 44 - ext.length - 1) + '.' + ext;
                    }
                    const uploadUrlRes = yield Axios.get(this.Media.path + "/upload-url", {
                        params: {
                            key: fileName,
                            contentType: file.type,
                            fileType: cbcFileType,
                        },
                        headers: { 'X-File-Type': cbcFileType }
                    });
                    const uploadUrl = extractUploadUrl(uploadUrlRes, 'media/upload-url (cbc)');
                    // Use XHR (not axios) to avoid default axios headers set by api lib, and to report upload progress.
                    yield putWithProgress(uploadUrl, file, onProgress);
                    return { filename: fileName };
                }
                else {
                    const ext = file.name.split('.').pop();
                    const nameWithoutExt = file.name.substring(0, file.name.length - ext.length - 1);
                    const fileName = `${nameWithoutExt}_${uuid()}.${ext}`;
                    const containerUrlRes = yield Axios.get(this.Media.path + "/upload-url", {
                        params: {
                            key: fileName,
                            contentType: file.type,
                        },
                    });
                    const containerUrl = extractUploadUrl(containerUrlRes, 'media/upload-url');
                    const containerClient = new ContainerClient(containerUrl);
                    const blobClient = containerClient.getBlockBlobClient(fileName);
                    yield blobClient.uploadData(file, {
                        blobHTTPHeaders: { blobContentType: file.type },
                        onProgress: onProgress ? (ev) => onProgress(ev.loadedBytes, file.size) : undefined,
                    });
                    return { filename: fileName };
                }
            })
        };
        this.Advertisers = {
            path: '/advertisers',
            find: (filter, signal) => __awaiter(this, void 0, void 0, function* () {
                const result = yield Axios.get(this.Advertisers.path + `?filter=${encodeURIComponent(JSON.stringify(filter))}`);
                return result.data;
            }),
            count: (filter) => __awaiter(this, void 0, void 0, function* () {
                if (!filter)
                    filter = {};
                const result = yield Axios.get(this.Advertisers.path + `/count?filter=${encodeURIComponent(JSON.stringify(filter))}`);
                return result.data;
            }),
            create: (advertiser) => __awaiter(this, void 0, void 0, function* () {
                const result = yield Axios.post(this.Advertisers.path, advertiser);
                return result.data;
            })
        };
        this.Uploads = {
            path: '/uploads',
            getPublicUrl: (filename, expireyDays = 1, download = false, fileType = 'data', requestId) => __awaiter(this, void 0, void 0, function* () {
                var _c;
                const reqUrl = new URL(this.Uploads.path + `/public-url/${encodeURIComponent(filename)}`, Axios.defaults.baseURL);
                reqUrl.searchParams.append('download', download.toString());
                reqUrl.searchParams.append('expiryDays', expireyDays.toString());
                const result = yield Axios.get(reqUrl.toString(), { headers: { 'X-File-Type': fileType, 'X-Request-Id': requestId !== null && requestId !== void 0 ? requestId : '' } });
                // The shared Axios error interceptor resolves failed requests with a bare `{ error }` shape
                // (no `.data`). Guard against that so we don't throw "Cannot read properties of undefined".
                if (result === null || result === void 0 ? void 0 : result.error) {
                    console.error("Error getting public url for uploaded file", result.error);
                    return { publicUrl: '' };
                }
                if ((_c = result === null || result === void 0 ? void 0 : result.data) === null || _c === void 0 ? void 0 : _c.error) {
                    console.error("Error getting public url for uploaded file", result.data.error);
                    return { publicUrl: '' };
                }
                return result.data;
            }),
            uploadFile: (file, fileType, requestId) => __awaiter(this, void 0, void 0, function* () {
                var _d;
                const apiSite = String(((_d = Axios.defaults.headers.common) === null || _d === void 0 ? void 0 : _d['source']) || this.site || '').toLowerCase();
                const cbcFileType = (fileType || 'custom_attachment');
                if (apiSite === 'cbc') {
                    // Sanitize the filename by removing/replacing problematic characters
                    const sanitizedName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
                    let fileName = `${Date.now()}_${sanitizedName}`;
                    // DB schema limits file name to 45 characters
                    if (fileName.length > 45) {
                        const ext = sanitizedName.split('.').pop();
                        fileName = fileName.substring(0, 44 - ext.length - 1) + '.' + ext;
                    }
                    const uploadUrlRes = yield Axios.get(this.Uploads.path + "/upload-url", {
                        params: {
                            key: fileName,
                            contentType: file.type,
                            fileType: cbcFileType,
                        },
                        headers: { 'X-File-Type': cbcFileType, 'X-Request-Id': requestId !== null && requestId !== void 0 ? requestId : '' }
                    });
                    const uploadUrl = extractUploadUrl(uploadUrlRes, 'uploads/upload-url (cbc)');
                    // Use fetch here to avoid default axios headers set by api lib.
                    const putRes = yield fetch(uploadUrl, { method: 'PUT', body: file, headers: { 'Content-Type': file.type } });
                    if (!putRes.ok) {
                        throw new Error(`Storage upload failed (${putRes.status} ${putRes.statusText})`);
                    }
                    return { filename: fileName };
                }
                else {
                    const ext = file.name.split('.').pop();
                    const nameWithoutExt = file.name.substring(0, file.name.length - ext.length - 1);
                    const fileName = `${nameWithoutExt}_${uuid()}.${ext}`;
                    const containerUrlRes = yield Axios.get(this.Media.path + "/upload-url", {
                        params: {
                            key: fileName,
                            contentType: file.type,
                        },
                    });
                    const containerUrl = extractUploadUrl(containerUrlRes, 'uploads/upload-url');
                    const containerClient = new ContainerClient(containerUrl);
                    const blobClient = containerClient.getBlockBlobClient(fileName);
                    yield blobClient.uploadData(file, { blobHTTPHeaders: { blobContentType: file.type } });
                    return { filename: fileName };
                }
            }),
            findById: (uploadId) => __awaiter(this, void 0, void 0, function* () {
                const result = yield Axios.get(this.Uploads.path + `/${uploadId}`);
                return result.data;
            }),
            adminTagFind: (tagId) => __awaiter(this, void 0, void 0, function* () {
                const result = yield Axios.get(this.Uploads.path + `/tag/${tagId}`);
                if (result.data.error) {
                    console.error("Error getting tag", result.data.error);
                    return { result: false, message: result.data.error.message };
                }
                return result.data;
            }),
            adminBannerFind: (bannerId) => __awaiter(this, void 0, void 0, function* () {
                const result = yield Axios.get(this.Uploads.path + `/banner/${bannerId}`);
                if (result.data.error) {
                    console.error("Error getting banner", result.data.error);
                    return { result: false, message: result.data.error.message };
                }
                return result.data;
            }),
            adminPreRollFind: (preRollId) => __awaiter(this, void 0, void 0, function* () {
                const result = yield Axios.get(this.Uploads.path + `/pre-roll/${preRollId}`);
                if (result.data.error) {
                    console.error("Error getting pre-roll", result.data.error);
                    return { result: false, message: result.data.error.message };
                }
                return result.data;
            }),
            update: (uploadId, upload) => __awaiter(this, void 0, void 0, function* () {
                const result = yield Axios.patch(this.Uploads.path + `/${uploadId}`, upload);
                return result.data;
            }),
            delete: (upload) => __awaiter(this, void 0, void 0, function* () {
                const result = yield Axios.post(this.Uploads.path + `/delete`, upload);
                return result.data;
            }),
        };
        this.User = {
            path: '/users',
            me: () => __awaiter(this, void 0, void 0, function* () {
                const result = yield Axios.get(this.User.path + "/me");
                if (result.data.error) {
                    return { result: true, message: result.data.error.message };
                }
                else {
                    return { result: true, user: result.data };
                }
            }),
            login: (credentials, siteOverride) => __awaiter(this, void 0, void 0, function* () {
                const rheaders = { 'Content-Type': 'application/json' };
                if (siteOverride) {
                    rheaders['site'] = siteOverride;
                }
                const result = yield Axios.post(this.User.path + "/login", credentials, { headers: rheaders });
                if (result.error) {
                    return { result: false, message: result.error.message };
                }
                else {
                    this.authToken = result.data.token;
                    Axios.defaults.headers.common['Authorization'] = `Bearer ${this.authToken}`;
                    const meResult = yield this.User.me();
                    if (meResult.user === undefined) {
                        return { result: false, message: 'Login failed' };
                    }
                    return { result: true, message: "success", user: meResult.user, token: this.authToken };
                }
            }),
            loginAzure: (id_token) => __awaiter(this, void 0, void 0, function* () {
                const result = yield Axios.get(this.User.path + "/aad-login?id_token=" + id_token);
                if (result.data.error) {
                    return { result: false, message: result.data.error.message };
                }
                else if (result.data.newUser === true) {
                    return { result: true, message: "new user", user: undefined, token: id_token, markets: result.data.markets };
                }
                else {
                    this.authToken = result.data.token;
                    Axios.defaults.headers.common['Authorization'] = `Bearer ${this.authToken}`;
                    const meResult = yield this.User.me();
                    if (meResult.user === undefined) {
                        return { result: false, message: 'Login failed' };
                    }
                    return { result: true, message: "success", user: meResult.user, token: this.authToken };
                }
            }),
            createAzure: (id_token, user) => __awaiter(this, void 0, void 0, function* () {
                const result = yield Axios.post(this.User.path + "/aad-create?id_token=" + id_token, user);
                if (result.data.error) {
                    return { result: false, message: result.data.error.message };
                }
                else {
                    this.authToken = result.data.token;
                    Axios.defaults.headers.common['Authorization'] = `Bearer ${this.authToken}`;
                    const meResult = yield this.User.me();
                    if (meResult.user === undefined) {
                        return { result: false, message: 'Login failed' };
                    }
                    return { result: true, message: "success", user: meResult.user, token: this.authToken };
                }
            }),
            registerWithKey: (object, key) => __awaiter(this, void 0, void 0, function* () {
                var _e, _f, _g, _h;
                try {
                    const result = yield Axios.post(this.User.path + "/register?key=" + key, object);
                    return result.data;
                }
                catch (error) {
                    // Format the error consistently with our interceptor
                    console.error("Register API error:", error);
                    // Format the error to match our expected ErrorResponse type
                    return {
                        error: {
                            statusCode: ((_e = error.response) === null || _e === void 0 ? void 0 : _e.status) || 500,
                            name: error.name || 'Error',
                            message: ((_h = (_g = (_f = error.response) === null || _f === void 0 ? void 0 : _f.data) === null || _g === void 0 ? void 0 : _g.error) === null || _h === void 0 ? void 0 : _h.message) ||
                                error.message ||
                                'An unexpected error occurred during registration'
                        }
                    };
                }
            }),
            find: (filter) => __awaiter(this, void 0, void 0, function* () {
                if (!filter)
                    filter = {};
                const result = yield Axios.get(this.User.path + `?filter=${encodeURIComponent(JSON.stringify(filter))}`);
                return result.data;
            }),
            roleChange: (userId, roleId, addRemove) => __awaiter(this, void 0, void 0, function* () {
                let result;
                if (addRemove) {
                    result = yield Axios.post(this.User.path + `/${userId}/roles?roleId=${roleId}`);
                }
                else {
                    result = yield Axios.delete(this.User.path + `/${userId}/roles?roleId=${roleId}`);
                }
                return result.data;
            }),
            managerList: (marketId, page, rows, count, search, orderBy, order, signal) => __awaiter(this, void 0, void 0, function* () {
                const result = yield Axios.get(this.User.path + "/roles?page=" + page + "&rows=" + rows + "&count=" + count + "&market=" + marketId + "&orderBy=" + orderBy + "&order=" + order + (search ? "&search=" + search : ""), { signal });
                if (count)
                    return result.data[0].Total;
                return result.data;
            }),
            findById: (id, filter) => __awaiter(this, void 0, void 0, function* () {
                if (!filter)
                    filter = {};
                const result = yield Axios.get(this.User.path + `/${id}?filter=${encodeURIComponent(JSON.stringify(filter))}`);
                return result.data;
            }),
            create: (object) => __awaiter(this, void 0, void 0, function* () {
                const result = yield Axios.post(this.User.path, object);
                return result.data;
            }),
            update: (id, object) => __awaiter(this, void 0, void 0, function* () {
                const result = yield Axios.patch(this.User.path + `/${id}`, object);
                return result.data;
            }),
            delete: (id) => __awaiter(this, void 0, void 0, function* () {
                throw new Error('Function not implemented.');
            }),
            count: (filter) => __awaiter(this, void 0, void 0, function* () {
                if (!filter)
                    filter = {};
                const result = yield Axios.get(this.User.path + `/count?filter=${encodeURIComponent(JSON.stringify(filter))}`);
                return result.data;
            }),
            startRegister: (accessKey) => __awaiter(this, void 0, void 0, function* () {
                const result = yield Axios.get(this.User.path + `/startregister?accessKey=${accessKey}`);
                return result.data;
            }),
            authorizeSpecAI: () => __awaiter(this, void 0, void 0, function* () {
                const result = yield Axios.get(this.User.path + `/authorize-specai`);
                return result.data;
            }),
            specaiCallback: (code) => __awaiter(this, void 0, void 0, function* () {
                const result = yield Axios.get(this.User.path + `/specai/callback?code=${code}`);
                return result.data;
            }),
            sendPasswordReset: (userId, whiteLabel) => __awaiter(this, void 0, void 0, function* () {
                const result = yield Axios.post(this.User.path + `/${userId}/send-password-reset`, { whiteLabel: whiteLabel });
                return result.data;
            }),
            setPassword: (userId, password, passwordConfirm) => __awaiter(this, void 0, void 0, function* () {
                const result = yield Axios.post(this.User.path + `/${userId}/set-password`, { password: password, passwordConfirm: passwordConfirm });
                return result.data;
            })
        };
        this.Campaign = {
            path: '/campaign',
            find(filter) {
                return __awaiter(this, void 0, void 0, function* () {
                    if (!filter)
                        filter = {};
                    const result = yield Axios.get(this.path + `?filter=${encodeURIComponent(JSON.stringify(filter))}`);
                    return result.data;
                });
            },
            findById(id, filter) {
                return __awaiter(this, void 0, void 0, function* () {
                    if (!filter)
                        filter = {};
                    const result = yield Axios.get(this.path + `/${id}?filter=${encodeURIComponent(JSON.stringify(filter))}`);
                    return result.data;
                });
            },
            create(object) {
                return __awaiter(this, void 0, void 0, function* () {
                    throw new Error('Function not implemented.');
                });
            },
            update(id, object) {
                return __awaiter(this, void 0, void 0, function* () {
                    throw new Error('Function not implemented.');
                });
            },
            delete(id) {
                return __awaiter(this, void 0, void 0, function* () {
                    throw new Error('Function not implemented.');
                });
            },
            count(filter) {
                return __awaiter(this, void 0, void 0, function* () {
                    if (!filter)
                        filter = {};
                    const result = yield Axios.get(this.path + `/count?filter=${encodeURIComponent(JSON.stringify(filter))}`);
                    return result.data;
                });
            },
            countByCategory(categoryId, filter) {
                return __awaiter(this, void 0, void 0, function* () {
                    if (!filter)
                        filter = {};
                    const result = yield Axios.get(this.path + `/countByCategory/${categoryId}?filter=${encodeURIComponent(JSON.stringify(filter))}`);
                    return result.data;
                });
            },
            /** Admin-only downloadable audio/script assets for a library spot */
            getAdminAssets(id) {
                return __awaiter(this, void 0, void 0, function* () {
                    const result = yield Axios.get(this.path + `/${id}/admin-assets`);
                    return result.error ? result : result.data;
                });
            }
        };
        this.CampaignFavorite = {
            path: '/campaignFavorite',
            find(filter) {
                return __awaiter(this, void 0, void 0, function* () {
                    if (!filter)
                        filter = {};
                    const result = yield Axios.get(this.path + `?filter=${encodeURIComponent(JSON.stringify(filter))}`);
                    return result.data;
                });
            },
            findById(id, filter) {
                return __awaiter(this, void 0, void 0, function* () {
                    if (!filter)
                        filter = {};
                    const result = yield Axios.get(this.path + `/${id}?filter=${encodeURIComponent(JSON.stringify(filter))}`);
                    return result.data;
                });
            },
            create(object) {
                return __awaiter(this, void 0, void 0, function* () {
                    const result = yield Axios.post(this.path, object);
                    return result.data;
                });
            },
            update(id, object) {
                return __awaiter(this, void 0, void 0, function* () {
                    throw new Error('Function not implemented.');
                });
            },
            delete(id) {
                return __awaiter(this, void 0, void 0, function* () {
                    const result = yield Axios.delete(this.path + `/${id}`);
                    return result.data;
                });
            }
        };
        this.Category = {
            path: '/category',
            find(filter) {
                return __awaiter(this, void 0, void 0, function* () {
                    if (!filter)
                        filter = {};
                    const result = yield Axios.get(this.path + `?filter=${encodeURIComponent(JSON.stringify(filter))}`);
                    return result.data;
                });
            },
            findById(id, filter) {
                return __awaiter(this, void 0, void 0, function* () {
                    if (!filter)
                        filter = {};
                    const result = yield Axios.get(this.path + `/${id}?filter=${encodeURIComponent(JSON.stringify(filter))}`);
                    return result.data;
                });
            },
            create(object) {
                return __awaiter(this, void 0, void 0, function* () {
                    const result = yield Axios.post(this.path, object);
                    return result.data;
                });
            },
            update(id, object) {
                return __awaiter(this, void 0, void 0, function* () {
                    const result = yield Axios.patch(this.path + `/${id}`, object);
                    return result.data;
                });
            },
            delete(id) {
                return __awaiter(this, void 0, void 0, function* () {
                    throw new Error('Function not implemented.');
                });
            },
            count(filter) {
                return __awaiter(this, void 0, void 0, function* () {
                    if (!filter)
                        filter = {};
                    const result = yield Axios.get(this.path + `/count?filter=${encodeURIComponent(JSON.stringify(filter))}`);
                    return result.data;
                });
            },
            companyCategoryChange(companyId, categoryId, addRemove) {
                return __awaiter(this, void 0, void 0, function* () {
                    let result;
                    if (addRemove) {
                        result = yield Axios.post(this.path + `/companies/${companyId}?categoryId=${categoryId}`);
                    }
                    else {
                        result = yield Axios.delete(this.path + `/companies/${companyId}?categoryId=${categoryId}`);
                    }
                    return result.data;
                });
            },
            findByCategoryId(categoryId) {
                return __awaiter(this, void 0, void 0, function* () {
                    const result = yield Axios.get(this.path + `/${categoryId}/companies`);
                    return result.data;
                });
            },
            // async companyCategoryChange(companyIds: Array<number>, categoryId: ID, addRemove: boolean): Promise<void | ErrorResponse> {
            //     let result
            //     if (addRemove) {
            //         result = await Axios.post(this.path + `/companies/${categoryId}?companyIds=${encodeURIComponent(JSON.stringify(companyIds))}`)
            //     } else {
            //         result = await Axios.delete(this.path + `/companies/${categoryId}?companyIds=${encodeURIComponent(JSON.stringify(companyIds))}`)
            //     }
            //     return result.data
            // }
        };
        this.ConciergeService = {
            path: '/conciergeService',
            find(filter) {
                return __awaiter(this, void 0, void 0, function* () {
                    if (!filter)
                        filter = {};
                    const reuslt = yield Axios.get(this.path + `?filter=${encodeURIComponent(JSON.stringify(filter))}`);
                    return reuslt.data;
                });
            },
            findById(id, filter) {
                return __awaiter(this, void 0, void 0, function* () {
                    if (!filter)
                        filter = {};
                    const result = yield Axios.get(this.path + `/${id}?filter=${encodeURIComponent(JSON.stringify(filter))}`);
                    return result.data;
                });
            },
            create(object) {
                return __awaiter(this, void 0, void 0, function* () {
                    const result = yield Axios.post(this.path, object);
                    return result.data;
                });
            },
            update(id, object) {
                return __awaiter(this, void 0, void 0, function* () {
                    const result = yield Axios.patch(this.path + `/${id}`, object);
                    return result.data;
                });
            },
            delete(id) {
                return __awaiter(this, void 0, void 0, function* () {
                    const result = yield Axios.delete(this.path + `/${id}`);
                    return result.data;
                });
            }
        };
        this.Concierge = {
            path: '/concierge',
            find(filter) {
                return __awaiter(this, void 0, void 0, function* () {
                    if (!filter)
                        filter = {};
                    const result = yield Axios.get(this.path + `?filter=${encodeURIComponent(JSON.stringify(filter))}`);
                    return result.data;
                });
            },
            findById(id, filter) {
                return __awaiter(this, void 0, void 0, function* () {
                    if (!filter)
                        filter = {};
                    const result = yield Axios.get(this.path + `/${id}?filter=${encodeURIComponent(JSON.stringify(filter))}`);
                    return result.data;
                });
            },
            create(object) {
                return __awaiter(this, void 0, void 0, function* () {
                    const result = yield Axios.post(this.path, object);
                    // The shared response interceptor resolves failed requests as a bare `{ error }`
                    // object (no `.data`). Return it so callers can show the backend's message
                    // instead of receiving `undefined`.
                    if (result === null || result === void 0 ? void 0 : result.error)
                        return result;
                    return result.data;
                });
            },
            update(id, object) {
                return __awaiter(this, void 0, void 0, function* () {
                    const result = yield Axios.patch(this.path + `/${id}`, object);
                    return result.data;
                });
            },
            revise(id, object) {
                return __awaiter(this, void 0, void 0, function* () {
                    const result = yield Axios.patch(this.path + `/${id}`, object);
                    return result.data;
                });
            },
            newAssets(id, object) {
                return __awaiter(this, void 0, void 0, function* () {
                    const result = yield Axios.patch(this.path + `/newassets/${id}`, object);
                    return result.data;
                });
            },
            delete(id) {
                return __awaiter(this, void 0, void 0, function* () {
                    const result = yield Axios.delete(this.path + `/${id}`);
                    return result.data;
                });
            },
            count(filter) {
                return __awaiter(this, void 0, void 0, function* () {
                    if (!filter)
                        filter = {};
                    const result = yield Axios.get(this.path + `/count?filter=${encodeURIComponent(JSON.stringify(filter))}`);
                    return result.data;
                });
            },
            upload(id, files) {
                return __awaiter(this, void 0, void 0, function* () {
                    const result = yield Axios.post(`uploads/concierge/${id}`, files);
                    return result.data;
                });
            },
            deleteUpload(id, upload) {
                return __awaiter(this, void 0, void 0, function* () {
                    const result = yield Axios.post(`uploads/concierge/delete/${id}`, upload);
                    return result.data;
                });
            },
            deliver(data) {
                return __awaiter(this, void 0, void 0, function* () {
                    const result = yield Axios.post(`uploads/concierge/deliver`, data);
                    return result.data;
                });
            },
            generate(prompts) {
                return __awaiter(this, void 0, void 0, function* () {
                    const result = yield Axios.post(this.path + `/generate`, prompts);
                    return result.data;
                });
            },
            approveRevise(id, name, approved, note, files) {
                return __awaiter(this, void 0, void 0, function* () {
                    const result = yield Axios.post(this.path + `/approve-revise/${id}`, { name, approved, note, files });
                    return result.data;
                });
            }
        };
        this.ConciergeSold = {
            path: '/conciergeSold',
            find(filter) {
                return __awaiter(this, void 0, void 0, function* () {
                    if (!filter)
                        filter = {};
                    const result = yield Axios.get(this.path + `?filter=${encodeURIComponent(JSON.stringify(filter))}`);
                    return result.data;
                });
            },
            findById(id, filter) {
                return __awaiter(this, void 0, void 0, function* () {
                    if (!filter)
                        filter = {};
                    const result = yield Axios.get(this.path + `/${id}?filter=${encodeURIComponent(JSON.stringify(filter))}`);
                    return result.data;
                });
            },
            create(object) {
                return __awaiter(this, void 0, void 0, function* () {
                    const result = yield Axios.post(this.path, object);
                    return result.data;
                });
            },
            update(id, object) {
                return __awaiter(this, void 0, void 0, function* () {
                    const result = yield Axios.patch(this.path + `/${id}`, object);
                    return result.data;
                });
            },
            delete(id) {
                return __awaiter(this, void 0, void 0, function* () {
                    const result = yield Axios.delete(this.path + `/${id}`);
                    return result.data;
                });
            }
        };
        this.ConciergeQuestionConciergeService = {
            path: '/concierge-question-concierge-service',
            find(filter) {
                return __awaiter(this, void 0, void 0, function* () {
                    if (!filter)
                        filter = {};
                    const result = yield Axios.get(this.path + `?filter=${encodeURIComponent(JSON.stringify(filter))}`);
                    return result.data;
                });
            },
            findById(id, filter) {
                return __awaiter(this, void 0, void 0, function* () {
                    if (!filter)
                        filter = {};
                    const result = yield Axios.get(this.path + `/${id}?filter=${encodeURIComponent(JSON.stringify(filter))}`);
                    return result.data;
                });
            },
            create(object) {
                return __awaiter(this, void 0, void 0, function* () {
                    const result = yield Axios.post(this.path, object);
                    return result.data;
                });
            },
            update(id, object) {
                return __awaiter(this, void 0, void 0, function* () {
                    const result = yield Axios.patch(this.path + `/${id}`, object);
                    return result.data;
                });
            },
            delete(id) {
                return __awaiter(this, void 0, void 0, function* () {
                    throw new Error('Function not implemented.');
                });
            },
            count(filter) {
                return __awaiter(this, void 0, void 0, function* () {
                    if (!filter)
                        filter = {};
                    const result = yield Axios.get(this.path + `/count?filter=${encodeURIComponent(JSON.stringify(filter))}`);
                    return result.data;
                });
            },
        };
        this.ConciergeQuestion = {
            path: '/concierge-question',
            find(filter) {
                return __awaiter(this, void 0, void 0, function* () {
                    if (!filter)
                        filter = {};
                    const result = yield Axios.get(this.path + `?filter=${encodeURIComponent(JSON.stringify(filter))}`);
                    return result.data;
                });
            },
            findById(id, filter) {
                return __awaiter(this, void 0, void 0, function* () {
                    if (!filter)
                        filter = {};
                    const result = yield Axios.get(this.path + `/${id}?filter=${encodeURIComponent(JSON.stringify(filter))}`);
                    return result.data;
                });
            },
            create(object) {
                return __awaiter(this, void 0, void 0, function* () {
                    const result = yield Axios.post(this.path, object);
                    return result.data;
                });
            },
            update(id, object) {
                return __awaiter(this, void 0, void 0, function* () {
                    const result = yield Axios.patch(this.path + `/${id}`, object);
                    return result.data;
                });
            },
            delete(id) {
                return __awaiter(this, void 0, void 0, function* () {
                    const result = yield Axios.delete(this.path + `/${id}`);
                    return result.data;
                });
            },
            count(filter) {
                return __awaiter(this, void 0, void 0, function* () {
                    if (!filter)
                        filter = {};
                    const result = yield Axios.get(this.path + `/count?filter=${encodeURIComponent(JSON.stringify(filter))}`);
                    return result.data;
                });
            },
        };
        this.Tag = {
            path: '/tag',
            find(filter = {}, marketId, signal) {
                return __awaiter(this, void 0, void 0, function* () {
                    if (!filter)
                        filter = {};
                    const result = yield Axios.get(this.path + `?filter=${encodeURIComponent(JSON.stringify(filter))}${marketId ? `&marketId=${marketId}` : ''}`);
                    return result.data;
                });
            },
            adminFind(filter = {}, signal) {
                return __awaiter(this, void 0, void 0, function* () {
                    if (!filter)
                        filter = {};
                    const result = yield Axios.get(this.path + `/admin?filter=${encodeURIComponent(JSON.stringify(filter))}`);
                    return result.data;
                });
            },
            findById(id, filter) {
                return __awaiter(this, void 0, void 0, function* () {
                    if (!filter)
                        filter = {};
                    const result = yield Axios.get(this.path + `/${id}?filter=${encodeURIComponent(JSON.stringify(filter))}`);
                    return result.data;
                });
            },
            create(object) {
                return __awaiter(this, void 0, void 0, function* () {
                    //@ts-ignore
                    const result = yield Axios.post(this.path, object);
                    return result.data;
                });
            },
            update(id, object) {
                return __awaiter(this, void 0, void 0, function* () {
                    const result = yield Axios.patch(this.path + `/${id}`, object);
                    return result.data;
                });
            },
            delete(id) {
                return __awaiter(this, void 0, void 0, function* () {
                    throw new Error('Function not implemented.');
                });
            },
            count(filter, marketId) {
                return __awaiter(this, void 0, void 0, function* () {
                    if (!filter)
                        filter = {};
                    const result = yield Axios.get(this.path + `/count?filter=${encodeURIComponent(JSON.stringify(filter))}${marketId ? `&marketId=${marketId}` : ''}`);
                    return result.data;
                });
            },
            reassign(id, producerEmail) {
                return __awaiter(this, void 0, void 0, function* () {
                    const result = yield Axios.post(this.path + `/${id}/reassign?email=${producerEmail}`);
                    return result.data;
                });
            },
            upload(id, files) {
                return __awaiter(this, void 0, void 0, function* () {
                    const result = yield Axios.post(`uploads/tag/${id}`, files);
                    return result.data;
                });
            },
            deleteUpload(id, upload) {
                return __awaiter(this, void 0, void 0, function* () {
                    const result = yield Axios.post(`uploads/tag/delete/${id}`, upload);
                    return result.data;
                });
            },
            deliver(data) {
                return __awaiter(this, void 0, void 0, function* () {
                    const result = yield Axios.post(`uploads/deliver/tag`, data);
                    return result.data;
                });
            },
            changeCreatedBy(id, newCreatorEmail) {
                return __awaiter(this, void 0, void 0, function* () {
                    const result = yield Axios.post(this.path + `/${id}/change-created-by?email=${encodeURIComponent(newCreatorEmail)}`);
                    return result.data;
                });
            },
        };
        this.CustomTagSold = {
            path: '/customTagSold',
            find(filter) {
                return __awaiter(this, void 0, void 0, function* () {
                    if (!filter)
                        filter = {};
                    const result = yield Axios.get(this.path + `?filter=${encodeURIComponent(JSON.stringify(filter))}`);
                    return result.data;
                });
            },
            findById(id, filter) {
                return __awaiter(this, void 0, void 0, function* () {
                    if (!filter)
                        filter = {};
                    const result = yield Axios.get(this.path + `/${id}?filter=${encodeURIComponent(JSON.stringify(filter))}`);
                    return result.data;
                });
            },
            create(object) {
                return __awaiter(this, void 0, void 0, function* () {
                    const result = yield Axios.post(this.path, object);
                    return result.data;
                });
            },
            update(id, object) {
                return __awaiter(this, void 0, void 0, function* () {
                    const result = yield Axios.patch(this.path + `/${id}`, object);
                    return result.data;
                });
            },
            delete(id) {
                return __awaiter(this, void 0, void 0, function* () {
                    const result = yield Axios.delete(this.path + `/${id}`);
                    return result.data;
                });
            }
        };
        this.CustomBanner = {
            path: '/customBanner',
            find(filter, signal) {
                return __awaiter(this, void 0, void 0, function* () {
                    if (!filter)
                        filter = {};
                    const result = yield Axios.get(this.path + `?filter=${encodeURIComponent(JSON.stringify(filter))}`, { signal: signal });
                    return result.data;
                });
            },
            adminFind(filter = {}, signal) {
                return __awaiter(this, void 0, void 0, function* () {
                    if (!filter)
                        filter = {};
                    const result = yield Axios.get(this.path + `/admin?filter=${encodeURIComponent(JSON.stringify(filter))}`);
                    return result.data;
                });
            },
            findById(id, filter, signal) {
                return __awaiter(this, void 0, void 0, function* () {
                    if (!filter)
                        filter = {};
                    const result = yield Axios.get(this.path + `/${id}?filter=${encodeURIComponent(JSON.stringify(filter))}`, { signal: signal });
                    return result.data;
                });
            },
            create(object, signal) {
                return __awaiter(this, void 0, void 0, function* () {
                    const result = yield Axios.post(this.path, object, { signal: signal });
                    return result.data;
                });
            },
            update(id, object, signal) {
                return __awaiter(this, void 0, void 0, function* () {
                    const result = yield Axios.patch(this.path + `/${id}`, object, { signal: signal });
                    return result.data;
                });
            },
            revise(id, object, notes, signal) {
                return __awaiter(this, void 0, void 0, function* () {
                    const result = yield Axios.patch(this.path + `/${id}/revise?notes=${notes}`, object, { signal: signal });
                    return result.data;
                });
            },
            delete(id, signal) {
                return __awaiter(this, void 0, void 0, function* () {
                    throw new Error('Function not implemented.');
                });
            },
            count(filter, signal) {
                return __awaiter(this, void 0, void 0, function* () {
                    if (!filter)
                        filter = {};
                    const result = yield Axios.get(this.path + `/count?filter=${encodeURIComponent(JSON.stringify(filter))}`, { signal: signal });
                    return result.data;
                });
            },
            upload(id, files) {
                return __awaiter(this, void 0, void 0, function* () {
                    const result = yield Axios.post(`uploads/banner/${id}`, files);
                    return result.data;
                });
            },
            deliver(data) {
                return __awaiter(this, void 0, void 0, function* () {
                    const result = yield Axios.post(`uploads/banner/deliver`, data);
                    return result.data;
                });
            },
            changeCreatedBy(id, newCreatorEmail) {
                return __awaiter(this, void 0, void 0, function* () {
                    const result = yield Axios.post(this.path + `/${id}/change-created-by?email=${encodeURIComponent(newCreatorEmail)}`);
                    return result.data;
                });
            },
        };
        this.CustomBannerSold = {
            path: '/customBannerSold',
            find(filter) {
                return __awaiter(this, void 0, void 0, function* () {
                    if (!filter)
                        filter = {};
                    const result = yield Axios.get(this.path + `?filter=${encodeURIComponent(JSON.stringify(filter))}`);
                    return result.data;
                });
            },
            findById(id, filter) {
                return __awaiter(this, void 0, void 0, function* () {
                    if (!filter)
                        filter = {};
                    const result = yield Axios.get(this.path + `/${id}?filter=${encodeURIComponent(JSON.stringify(filter))}`);
                    return result.data;
                });
            },
            create(object) {
                return __awaiter(this, void 0, void 0, function* () {
                    const result = yield Axios.post(this.path, object);
                    return result.data;
                });
            },
            update(id, object) {
                return __awaiter(this, void 0, void 0, function* () {
                    const result = yield Axios.patch(this.path + `/${id}`, object);
                    return result.data;
                });
            },
            delete(id) {
                return __awaiter(this, void 0, void 0, function* () {
                    const result = yield Axios.delete(this.path + `/${id}`);
                    return result.data;
                });
            }
        };
        this.CustomPreRoll = {
            path: '/customPreRoll',
            find(filter) {
                return __awaiter(this, void 0, void 0, function* () {
                    if (!filter)
                        filter = {};
                    const result = yield Axios.get(this.path + `?filter=${encodeURIComponent(JSON.stringify(filter))}`);
                    return result.data;
                });
            },
            adminFind(filter = {}, signal) {
                return __awaiter(this, void 0, void 0, function* () {
                    if (!filter)
                        filter = {};
                    const result = yield Axios.get(this.path + `/admin?filter=${encodeURIComponent(JSON.stringify(filter))}`);
                    return result.data;
                });
            },
            findById(id, filter) {
                return __awaiter(this, void 0, void 0, function* () {
                    if (!filter)
                        filter = {};
                    const result = yield Axios.get(this.path + `/${id}?filter=${encodeURIComponent(JSON.stringify(filter))}`);
                    return result.data;
                });
            },
            create(object) {
                return __awaiter(this, void 0, void 0, function* () {
                    const result = yield Axios.post(this.path, object);
                    return result.data;
                });
            },
            update(id, object) {
                return __awaiter(this, void 0, void 0, function* () {
                    const result = yield Axios.patch(this.path + `/${id}`, object);
                    return result.data;
                });
            },
            delete(id) {
                return __awaiter(this, void 0, void 0, function* () {
                    throw new Error('Function not implemented.');
                });
            },
            count(filter) {
                return __awaiter(this, void 0, void 0, function* () {
                    if (!filter)
                        filter = {};
                    const result = yield Axios.get(this.path + `/count?filter=${encodeURIComponent(JSON.stringify(filter))}`);
                    return result.data;
                });
            },
            upload(id, files) {
                return __awaiter(this, void 0, void 0, function* () {
                    const result = yield Axios.post(`uploads/preroll/${id}`, files);
                    return result.data;
                });
            },
            deliver(data) {
                return __awaiter(this, void 0, void 0, function* () {
                    const result = yield Axios.post(`uploads/preroll/deliver`, data);
                    return result.data;
                });
            },
        };
        this.CustomPreRollSold = {
            path: '/customPreRollSold',
            find(filter) {
                return __awaiter(this, void 0, void 0, function* () {
                    if (!filter)
                        filter = {};
                    const result = yield Axios.get(this.path + `?filter=${encodeURIComponent(JSON.stringify(filter))}`);
                    return result.data;
                });
            },
            findById(id, filter) {
                return __awaiter(this, void 0, void 0, function* () {
                    if (!filter)
                        filter = {};
                    const result = yield Axios.get(this.path + `/${id}?filter=${encodeURIComponent(JSON.stringify(filter))}`);
                    return result.data;
                });
            },
            create(object) {
                return __awaiter(this, void 0, void 0, function* () {
                    const result = yield Axios.post(this.path, object);
                    return result.data;
                });
            },
            update(id, object) {
                return __awaiter(this, void 0, void 0, function* () {
                    const result = yield Axios.patch(this.path + `/${id}`, object);
                    return result.data;
                });
            },
            delete(id) {
                return __awaiter(this, void 0, void 0, function* () {
                    const result = yield Axios.delete(this.path + `/${id}`);
                    return result.data;
                });
            }
        };
        this.CustomSpotRequest = {
            path: '/customSpotRequest',
            find(filter) {
                return __awaiter(this, void 0, void 0, function* () {
                    if (!filter)
                        filter = {};
                    const result = yield Axios.get(this.path + `?filter=${encodeURIComponent(JSON.stringify(filter))}`);
                    return result.data;
                });
            },
            findByMarket(marketId, filter) {
                return __awaiter(this, void 0, void 0, function* () {
                    if (!filter)
                        filter = {};
                    const result = yield Axios.get(this.path + `ByMarket/${marketId}?filter=${encodeURIComponent(JSON.stringify(filter))}`);
                    return result.data;
                });
            },
            findById(id, filter) {
                return __awaiter(this, void 0, void 0, function* () {
                    if (!filter)
                        filter = {};
                    const result = yield Axios.get(this.path + `/${id}?filter=${encodeURIComponent(JSON.stringify(filter))}`);
                    return result.data;
                });
            },
            create(object) {
                return __awaiter(this, void 0, void 0, function* () {
                    const result = yield Axios.post(this.path, object);
                    return result.data;
                });
            },
            update(id, object) {
                return __awaiter(this, void 0, void 0, function* () {
                    throw new Error('Function not implemented.');
                });
            },
            delete(id) {
                return __awaiter(this, void 0, void 0, function* () {
                    throw new Error('Function not implemented.');
                });
            },
            count(filter) {
                return __awaiter(this, void 0, void 0, function* () {
                    if (!filter)
                        filter = {};
                    const result = yield Axios.get(this.path + `/count?filter=${encodeURIComponent(JSON.stringify(filter))}`);
                    return result.data;
                });
            },
            upload(id, files) {
                return __awaiter(this, void 0, void 0, function* () {
                    const result = yield Axios.post(`uploads/customRequest/${id}`, files);
                    return result.data;
                });
            },
            deliver(data) {
                return __awaiter(this, void 0, void 0, function* () {
                    const result = yield Axios.post(`uploads/customRequest/deliver`, data);
                    return result.data;
                });
            }
        };
        this.OnDemand = {
            path: '/onDemand',
            find(filter) {
                return __awaiter(this, void 0, void 0, function* () {
                    if (!filter)
                        filter = {};
                    const result = yield Axios.get(this.path + `?filter=${encodeURIComponent(JSON.stringify(filter))}`);
                    return result.data;
                });
            },
            findById(id, filter) {
                return __awaiter(this, void 0, void 0, function* () {
                    if (!filter)
                        filter = {};
                    const result = yield Axios.get(this.path + `/${id}?filter=${encodeURIComponent(JSON.stringify(filter))}`);
                    return result.data;
                });
            },
            create(object) {
                return __awaiter(this, void 0, void 0, function* () {
                    const result = yield Axios.post(this.path, object);
                    return result.data;
                });
            },
            update(id, object) {
                return __awaiter(this, void 0, void 0, function* () {
                    throw new Error('Function not implemented.');
                });
            },
            delete(id) {
                return __awaiter(this, void 0, void 0, function* () {
                    throw new Error('Function not implemented.');
                });
            },
            count(filter) {
                return __awaiter(this, void 0, void 0, function* () {
                    if (!filter)
                        filter = {};
                    const result = yield Axios.get(this.path + `/count?filter=${encodeURIComponent(JSON.stringify(filter))}`);
                    return result.data;
                });
            }
        };
        this.Market = {
            path: '/market',
            find(filter, signal) {
                return __awaiter(this, void 0, void 0, function* () {
                    if (!filter)
                        filter = {};
                    const result = yield Axios.get(this.path + `?filter=${encodeURIComponent(JSON.stringify(filter))}`, { signal });
                    return result.data;
                });
            },
            findById(id, filter, signal) {
                return __awaiter(this, void 0, void 0, function* () {
                    if (!filter)
                        filter = {};
                    const result = yield Axios.get(this.path + `/${id}?filter=${encodeURIComponent(JSON.stringify(filter))}`, { signal });
                    return result.data;
                });
            },
            create(object, signal) {
                return __awaiter(this, void 0, void 0, function* () {
                    const result = yield Axios.post(this.path, object, { signal });
                    return result.data;
                });
            },
            update(id, object, signal) {
                return __awaiter(this, void 0, void 0, function* () {
                    const result = yield Axios.patch(this.path + `/${id}`, object, { signal });
                    return result.data;
                });
            },
            delete(id, signal) {
                return __awaiter(this, void 0, void 0, function* () {
                    throw new Error('Function not implemented.');
                });
            },
            count(filter, signal) {
                return __awaiter(this, void 0, void 0, function* () {
                    if (!filter)
                        filter = {};
                    const result = yield Axios.get(this.path + `/count?filter=${encodeURIComponent(JSON.stringify(filter))}`, { signal });
                    return result.data;
                });
            }
        };
        this.Producer = {
            path: '/producers',
            find(filter, signal) {
                return __awaiter(this, void 0, void 0, function* () {
                    if (!filter)
                        filter = {};
                    const result = yield Axios.get(this.path + `?filter=${encodeURIComponent(JSON.stringify(filter))}`, { signal });
                    return result.data;
                });
            },
            findById(id, filter, signal) {
                return __awaiter(this, void 0, void 0, function* () {
                    throw new Error('Function not implemented.');
                });
            },
            create(object, signal) {
                return __awaiter(this, void 0, void 0, function* () {
                    throw new Error('Function not implemented.');
                });
            },
            update(id, object, signal) {
                return __awaiter(this, void 0, void 0, function* () {
                    throw new Error('Function not implemented.');
                });
            },
            delete(id, signal) {
                return __awaiter(this, void 0, void 0, function* () {
                    throw new Error('Function not implemented.');
                });
            }
        };
        this.Company = {
            path: '/company',
            find(filter) {
                return __awaiter(this, void 0, void 0, function* () {
                    if (!filter)
                        filter = {};
                    const result = yield Axios.get(this.path + `?filter=${encodeURIComponent(JSON.stringify(filter))}`);
                    return result.data;
                });
            },
            findById(id, filter) {
                return __awaiter(this, void 0, void 0, function* () {
                    if (!filter)
                        filter = {};
                    const result = yield Axios.get(this.path + `/${id}?filter=${encodeURIComponent(JSON.stringify(filter))}`);
                    return result.data;
                });
            },
            create(object) {
                return __awaiter(this, void 0, void 0, function* () {
                    const result = yield Axios.post(this.path, object);
                    return result.data;
                });
            },
            update(id, object) {
                return __awaiter(this, void 0, void 0, function* () {
                    const result = yield Axios.patch(this.path + `/${id}`, object);
                    return result.data;
                });
            },
            delete(id) {
                return __awaiter(this, void 0, void 0, function* () {
                    throw new Error('Function not implemented.');
                });
            },
            count(filter) {
                return __awaiter(this, void 0, void 0, function* () {
                    if (!filter)
                        filter = {};
                    const result = yield Axios.get(this.path + `/count?filter=${encodeURIComponent(JSON.stringify(filter))}`);
                    return result.data;
                });
            },
            getRegistrationKey(id) {
                return __awaiter(this, void 0, void 0, function* () {
                    const result = yield Axios.get(this.path + `/${id}/access-key`);
                    return result.data;
                });
            }
        };
        this.TrackedItem = {
            path: '/trackedItem',
            find(filter) {
                return __awaiter(this, void 0, void 0, function* () {
                    if (!filter)
                        filter = {};
                    const result = yield Axios.get(this.path + `?filter=${encodeURIComponent(JSON.stringify(filter))}`);
                    return result.data;
                });
            },
            findById(id, filter) {
                return __awaiter(this, void 0, void 0, function* () {
                    if (!filter)
                        filter = {};
                    const result = yield Axios.get(this.path + `/${id}?filter=${encodeURIComponent(JSON.stringify(filter))}`);
                    return result.data;
                });
            },
            create(object) {
                return __awaiter(this, void 0, void 0, function* () {
                    const result = yield Axios.post(this.path, object);
                    return result.data;
                });
            },
            update(id, object) {
                return __awaiter(this, void 0, void 0, function* () {
                    const result = yield Axios.patch(this.path + '/' + id, object);
                    return result.data;
                });
            },
            delete(id) {
                return __awaiter(this, void 0, void 0, function* () {
                    throw new Error('Function not implemented.');
                });
            },
            count(filter) {
                return __awaiter(this, void 0, void 0, function* () {
                    if (!filter)
                        filter = {};
                    const result = yield Axios.get(this.path + `/count?filter=${encodeURIComponent(JSON.stringify(filter))}`);
                    return result.data;
                });
            },
        };
        this.CustomExecutionElement = {
            path: '/customExecutionElement',
            find(filter) {
                return __awaiter(this, void 0, void 0, function* () {
                    if (!filter)
                        filter = {};
                    const result = yield Axios.get(this.path + `?filter=${encodeURIComponent(JSON.stringify(filter))}`);
                    return result.data;
                });
            },
            findById(id, filter) {
                return __awaiter(this, void 0, void 0, function* () {
                    if (!filter)
                        filter = {};
                    const result = yield Axios.get(this.path + `/${id}?filter=${encodeURIComponent(JSON.stringify(filter))}`);
                    return result.data;
                });
            },
            create(object) {
                return __awaiter(this, void 0, void 0, function* () {
                    throw new Error('Function not implemented.');
                });
            },
            update(id, object) {
                return __awaiter(this, void 0, void 0, function* () {
                    throw new Error('Function not implemented.');
                });
            },
            delete(id) {
                return __awaiter(this, void 0, void 0, function* () {
                    throw new Error('Function not implemented.');
                });
            }
        };
        this.ConciergeAuthorizedCompany = {
            path: '/conciergeAuthorizedCompany',
            find(filter) {
                return __awaiter(this, void 0, void 0, function* () {
                    throw new Error('Function not implemented.');
                });
            },
            findById(id, filter) {
                return __awaiter(this, void 0, void 0, function* () {
                    throw new Error('Function not implemented.');
                });
            },
            create(object) {
                return __awaiter(this, void 0, void 0, function* () {
                    throw new Error('Function not implemented.');
                });
            },
            update(id, object) {
                return __awaiter(this, void 0, void 0, function* () {
                    throw new Error('Function not implemented.');
                });
            },
            delete(id) {
                return __awaiter(this, void 0, void 0, function* () {
                    throw new Error('Function not implemented.');
                });
            },
            findByCompanyId(companyId, filter) {
                return __awaiter(this, void 0, void 0, function* () {
                    if (!filter)
                        filter = {};
                    const result = yield Axios.get(this.path + `/${companyId}?filter=${encodeURIComponent(JSON.stringify(filter))}`);
                    return result.data;
                });
            }
        };
        this.ConciergeAuthorizedMarket = {
            path: '/concierge-authorized-market',
            find(filter) {
                return __awaiter(this, void 0, void 0, function* () {
                    const result = yield Axios.get(this.path + `?filter=${encodeURIComponent(JSON.stringify(filter))}`);
                    return result.data;
                });
            },
            findById(id, filter) {
                return __awaiter(this, void 0, void 0, function* () {
                    if (!filter)
                        filter = {};
                    const result = yield Axios.get(this.path + `/${id}?filter=${encodeURIComponent(JSON.stringify(filter))}`);
                    return result.data;
                });
            },
            create(object) {
                return __awaiter(this, void 0, void 0, function* () {
                    const result = yield Axios.post(this.path, object);
                    return result.data;
                });
            },
            update(id, object) {
                return __awaiter(this, void 0, void 0, function* () {
                    const result = yield Axios.patch(this.path + `/${id}`, object);
                    return result.data;
                });
            },
            delete(id) {
                return __awaiter(this, void 0, void 0, function* () {
                    const result = yield Axios.delete(this.path + `/${id}`);
                    return result.data;
                });
            },
        };
        this.Account = {
            path: '/account',
            sendEmailConfirmation(userId) {
                return __awaiter(this, void 0, void 0, function* () {
                    yield Axios.post(this.path + `/sendEmailConfirmation`, { userId: userId });
                });
            },
            confirmEmail(id, token) {
                return __awaiter(this, void 0, void 0, function* () {
                    const result = yield Axios.get(this.path + `/confirmEmail?userId=${id}&token=${token}`);
                    return result.data;
                });
            },
            sendPasswordReset(email, whiteLabel) {
                return __awaiter(this, void 0, void 0, function* () {
                    const result = yield Axios.post(this.path + `/sendPasswordReset`, { email: email, whiteLabel: whiteLabel });
                    return result.data;
                });
            },
            resetPassword(userId, token, password, passwordConfirm) {
                return __awaiter(this, void 0, void 0, function* () {
                    const result = yield Axios.post(this.path + `/resetPassword`, { userId: userId, token: token, password: password, passwordConfirm: passwordConfirm });
                    return result.data;
                });
            }
        };
        this.SiteAnnouncement = {
            path: '/site-announcement',
            get(signal) {
                return __awaiter(this, void 0, void 0, function* () {
                    const result = yield Axios.get(this.path + "/active", { signal });
                    return result.data;
                });
            },
            getLast(signal) {
                return __awaiter(this, void 0, void 0, function* () {
                    const result = yield Axios.get(this.path + "/last", { signal });
                    return result.data;
                });
            },
            save(announcement, signal) {
                return __awaiter(this, void 0, void 0, function* () {
                    const result = yield Axios.post(this.path, announcement, { signal });
                    return result.data;
                });
            }
        };
        this.Reports = {
            path: '/reports',
            federationRevenueYTD(signal) {
                return __awaiter(this, void 0, void 0, function* () {
                    const result = yield Axios.get(this.path + "/federation-revenue-ytd", { signal });
                    return result.data;
                });
            },
            quarterlySales(marketId, dateStart, dateEnd, signal) {
                return __awaiter(this, void 0, void 0, function* () {
                    const result = yield Axios.get(this.path + "/quarterly-sales?market=" + marketId + (dateStart ? `&dateStart=${dateStart}` : "") + (dateEnd ? `&dateEnd=${dateEnd}` : ""), { signal });
                    return result.data;
                });
            },
            salesByUser(marketId, startDate, endDate, signal) {
                return __awaiter(this, void 0, void 0, function* () {
                    const result = yield Axios.get(this.path
                        + "/sales-by-user?market=" + marketId
                        + "&startDate=" + `${startDate.getFullYear()}-${(startDate.getMonth() + 1).toString().padStart(2, '0')}-${startDate.getDate().toString().padStart(2, '0')}`
                        + "&endDate=" + `${endDate.getFullYear()}-${(endDate.getMonth() + 1).toString().padStart(2, '0')}-${endDate.getDate().toString().padStart(2, '0')}`, { signal });
                    return result.data;
                });
            },
            revenue(companyId, dateStart, dateEnd, orderBy, order, page, rows, count, signal) {
                return __awaiter(this, void 0, void 0, function* () {
                    const result = yield Axios.get(this.path + "/revenue?page=" + page + "&rows=" + rows + "&count=" + count + "&" + (companyId ? `federation=${companyId}` + (dateStart || dateEnd || orderBy || order ? "&" : "") : "") + (dateStart ? `startDate=${dateStart}` + (dateEnd || orderBy || order ? "&" : "") : "") + (dateEnd ? `endDate=${dateEnd}` + (orderBy || order ? "&" : "") : "") + (orderBy ? `orderBy=${orderBy}` + (order ? "&" : "") : "") + (order ? `order=${order}` : ""), { signal });
                    return result.data;
                });
            },
            getSiteSales(startDate, endDate, companyId) {
                return __awaiter(this, void 0, void 0, function* () {
                    const dateStart = `${startDate.getFullYear()}-${(startDate.getMonth() + 1).toString().padStart(2, '0')}-${startDate.getDate().toString().padStart(2, '0')}`;
                    const dateEnd = `${endDate.getFullYear()}-${(endDate.getMonth() + 1).toString().padStart(2, '0')}-${(endDate.getDate()).toString().padStart(2, '0')}`;
                    const result = yield Axios.post(this.path + `/get-site-sales`, { companyId, startDate: dateStart, endDate: dateEnd });
                    return result.data;
                });
            },
            revenueSums(companyId, dateStart, dateEnd, count, signal) {
                return __awaiter(this, void 0, void 0, function* () {
                    const result = yield Axios.get(this.path + "/revenue-sums?count=" + count + "&" + (companyId ? `federation=${companyId}` + (dateStart || dateEnd ? "&" : "") : "") + (dateStart ? `startDate=${dateStart}` + (dateEnd ? "&" : "") : "") + (dateEnd ? `endDate=${dateEnd}` : ""), { signal });
                    return result.data;
                });
            },
            adminTagList(filter, signal) {
                return __awaiter(this, void 0, void 0, function* () {
                    const result = yield Axios.get(this.path + "/admin/tag-list?filter=" + encodeURIComponent(JSON.stringify(filter)), { signal });
                    return result.data;
                });
            },
            adminTagListCount(filter, signal) {
                return __awaiter(this, void 0, void 0, function* () {
                    const result = yield Axios.get(this.path + "/admin/tag-list/count?filter=" + encodeURIComponent(JSON.stringify(filter)), { signal });
                    return result.data;
                });
            },
            adminBannerList(filter, signal) {
                return __awaiter(this, void 0, void 0, function* () {
                    const result = yield Axios.get(this.path + "/admin/banner-list?filter=" + encodeURIComponent(JSON.stringify(filter)), { signal });
                    return result.data;
                });
            },
            adminBannerListCount(filter, signal) {
                return __awaiter(this, void 0, void 0, function* () {
                    const result = yield Axios.get(this.path + "/admin/banner-list/count?filter=" + encodeURIComponent(JSON.stringify(filter)), { signal });
                    return result.data;
                });
            },
            adminCustomList(filter, signal) {
                return __awaiter(this, void 0, void 0, function* () {
                    const result = yield Axios.get(this.path + "/admin/custom-list?filter=" + encodeURIComponent(JSON.stringify(filter)), { signal });
                    return result.data;
                });
            },
            adminCustomListCount(filter, signal) {
                return __awaiter(this, void 0, void 0, function* () {
                    const result = yield Axios.get(this.path + "/admin/custom-list/count?filter=" + encodeURIComponent(JSON.stringify(filter)), { signal });
                    return result.data;
                });
            },
            getAdminDailySalesOverview() {
                return __awaiter(this, void 0, void 0, function* () {
                    const result = yield Axios.get(this.path + "/admin-daily-sales-overview");
                    return result.data;
                });
            },
            getCompanyDailySalesOverview(companyId) {
                return __awaiter(this, void 0, void 0, function* () {
                    const result = yield Axios.get(this.path + `/company-daily-sales-overview/${companyId}`);
                    return result.data;
                });
            },
            getCompanyDailySalesOverviewMarketFiltered(companyId, markets) {
                return __awaiter(this, void 0, void 0, function* () {
                    const result = yield Axios.post(this.path + `/company-daily-sales-market-filtered`, { companyId, markets });
                    return result.data;
                });
            },
            getCompanyDailyMarketSales(companyId) {
                return __awaiter(this, void 0, void 0, function* () {
                    const result = yield Axios.get(this.path + `/company-daily-market-sales/${companyId}`);
                    return result.data;
                });
            },
            getCompanyTopMonthlyMarketSales(companyId) {
                return __awaiter(this, void 0, void 0, function* () {
                    const result = yield Axios.get(this.path + `/company-top-monthly-market-sales/${companyId}`);
                    return result.data;
                });
            },
            getCompanyMonthlyMarketSales(companyId) {
                return __awaiter(this, void 0, void 0, function* () {
                    const result = yield Axios.get(this.path + `/company-monthly-market-sales/${companyId}`);
                    return result.data;
                });
            },
            getCustomSalesReport(salesReports, startDate, endDate, companyId, markets) {
                return __awaiter(this, void 0, void 0, function* () {
                    const dateStart = `${startDate.getFullYear()}-${(startDate.getMonth() + 1).toString().padStart(2, '0')}-${startDate.getDate().toString().padStart(2, '0')}`;
                    const dateEnd = `${endDate.getFullYear()}-${(endDate.getMonth() + 1).toString().padStart(2, '0')}-${(endDate.getDate()).toString().padStart(2, '0')}`;
                    const result = yield Axios.post(this.path + `/custom-sales-report`, { salesReports, dateStart, dateEnd, companyId, markets });
                    return result.data;
                });
            },
            getRequestCounts(companyId, startDate, endDate, markets) {
                return __awaiter(this, void 0, void 0, function* () {
                    const dateStart = `${startDate.getFullYear()}-${(startDate.getMonth() + 1).toString().padStart(2, '0')}-${startDate.getDate().toString().padStart(2, '0')}`;
                    const dateEnd = `${endDate.getFullYear()}-${(endDate.getMonth() + 1).toString().padStart(2, '0')}-${(endDate.getDate()).toString().padStart(2, '0')}`;
                    const result = yield Axios.post(this.path + `/request-counts`, { companyId, dateStart, dateEnd, markets });
                    return result.data;
                });
            },
            getRequestReportTableCounts(companyId, startDate, endDate, order) {
                return __awaiter(this, void 0, void 0, function* () {
                    const dateStart = `${startDate.getFullYear()}-${(startDate.getMonth() + 1).toString().padStart(2, '0')}-${startDate.getDate().toString().padStart(2, '0')}`;
                    const dateEnd = `${endDate.getFullYear()}-${(endDate.getMonth() + 1).toString().padStart(2, '0')}-${(endDate.getDate()).toString().padStart(2, '0')}`;
                    const result = yield Axios.post(this.path + `/request-report-table-counts`, { companyId, dateStart, dateEnd, order });
                    return result.data;
                });
            },
            getCustomRequestsReport(requestsReports, startDate, endDate, companyId, markets) {
                return __awaiter(this, void 0, void 0, function* () {
                    const dateStart = `${startDate.getFullYear()}-${(startDate.getMonth() + 1).toString().padStart(2, '0')}-${startDate.getDate().toString().padStart(2, '0')}`;
                    const dateEnd = `${endDate.getFullYear()}-${(endDate.getMonth() + 1).toString().padStart(2, '0')}-${(endDate.getDate()).toString().padStart(2, '0')}`;
                    const result = yield Axios.post(this.path + `/custom-requests-report`, { requestsReports, dateStart, dateEnd, companyId, markets });
                    return result.data;
                });
            },
            getCompanyUserRequestCounts(companyId, startDate, endDate, order, page, rows) {
                return __awaiter(this, void 0, void 0, function* () {
                    const dateStart = `${startDate.getFullYear()}-${(startDate.getMonth() + 1).toString().padStart(2, '0')}-${startDate.getDate().toString().padStart(2, '0')}`;
                    const dateEnd = `${endDate.getFullYear()}-${(endDate.getMonth() + 1).toString().padStart(2, '0')}-${(endDate.getDate()).toString().padStart(2, '0')}`;
                    const result = yield Axios.post(this.path + `/company-user-request-counts`, { companyId, dateStart, dateEnd, order, page, rows });
                    return result.data;
                });
            },
        };
        this.OpenAi = {
            path: '/openai',
            generateScript(prompts) {
                return __awaiter(this, void 0, void 0, function* () {
                    const result = yield Axios.post(this.path + `/generate`, prompts);
                    return result.data;
                });
            },
            wizardSuggestions(body) {
                return __awaiter(this, void 0, void 0, function* () {
                    const result = yield Axios.post(this.path + `/wizard/suggestions`, body);
                    return result.data;
                });
            },
            wizardRewrite(body) {
                return __awaiter(this, void 0, void 0, function* () {
                    const result = yield Axios.post(this.path + `/wizard/rewrite`, body);
                    return result.data;
                });
            },
            wizardCheckScript(body) {
                return __awaiter(this, void 0, void 0, function* () {
                    const result = yield Axios.post(this.path + `/wizard/check-script`, body);
                    return result.data;
                });
            },
        };
        this.Role = {
            path: '/role',
            find(signal) {
                return __awaiter(this, void 0, void 0, function* () {
                    const result = yield Axios.get(this.path, { signal });
                    return result.data;
                });
            },
        };
        this.RegionalUser = {
            path: '/regional-users',
            myMarkets(signal) {
                return __awaiter(this, void 0, void 0, function* () {
                    const result = yield Axios.get(this.path + '/my-markets', { signal });
                    return result.data;
                });
            },
            list(userId, signal) {
                return __awaiter(this, void 0, void 0, function* () {
                    const query = userId ? `?userId=${encodeURIComponent(userId)}` : '';
                    const result = yield Axios.get(this.path + query, { signal });
                    return result.data;
                });
            },
            setMarkets(userId, marketIds, signal) {
                return __awaiter(this, void 0, void 0, function* () {
                    const result = yield Axios.put(this.path + `/${encodeURIComponent(userId)}`, { marketIds }, { signal });
                    return result.data;
                });
            },
        };
        this.AdminUtils = {
            path: '/admin-utils',
            syncAddUserMarkets() {
                return __awaiter(this, void 0, void 0, function* () {
                    const result = yield Axios.post(this.path + "/update-user-additional-markets");
                    return result.data;
                });
            },
            changeAdditionalUserMarkets(marketsToAdd, marketsToRemove, userEmail) {
                return __awaiter(this, void 0, void 0, function* () {
                    const result = yield Axios.post(this.path + "/change-user-additional-markets", { marketsToAdd, marketsToRemove, userEmail });
                    return result.data;
                });
            },
            markTagCompleted(tagAssignmentId, reason, tagId, signal) {
                return __awaiter(this, void 0, void 0, function* () {
                    const result = yield Axios.post(this.path + "/mark-tag-completed", { tagAssignmentId: tagAssignmentId, reason: reason, tagId: tagId }, { signal });
                    return result.data;
                });
            },
            resendConciergeToVPro(conciergeId, signal) {
                return __awaiter(this, void 0, void 0, function* () {
                    const result = yield Axios.post(this.path + "/concierge/vpro-resend", { conciergeId: conciergeId }, { signal });
                    return result.data;
                });
            },
            resendTagToVpro(tagId, signal) {
                return __awaiter(this, void 0, void 0, function* () {
                    const result = yield Axios.post(this.path + "/tag/vpro-resend", { tagId: tagId }, { signal });
                    return result.data;
                });
            },
            resendBannerToVpro(bannerId, signal) {
                return __awaiter(this, void 0, void 0, function* () {
                    const result = yield Axios.post(this.path + "/banner/vpro-resend", { bannerId: bannerId }, { signal });
                    return result.data;
                });
            },
            resendPreRollToVpro(preRollId, signal) {
                return __awaiter(this, void 0, void 0, function* () {
                    const result = yield Axios.post(this.path + "/pre-roll/vpro-resend", { preRollId: preRollId }, { signal });
                    return result.data;
                });
            },
        };
        this.Enco = {
            path: '/enco',
            createPreviewAudio(data) {
                return __awaiter(this, void 0, void 0, function* () {
                    const result = yield Axios.post(this.path + "/preview", data);
                    return result.data;
                });
            },
        };
        this.Station = {
            path: '/station',
            find(filter = {}, signal) {
                return __awaiter(this, void 0, void 0, function* () {
                    const result = yield Axios.get(this.path + `?filter=${encodeURIComponent(JSON.stringify(filter))}`, { signal });
                    return result.data;
                });
            },
            findById(id, filter, signal) {
                return __awaiter(this, void 0, void 0, function* () {
                    const result = yield Axios.get(this.path + `/${id}?filter=${encodeURIComponent(JSON.stringify(filter))}`, { signal });
                    return result.data;
                });
            },
        };
        this.Stations = {
            path: '/admin/stations',
            find: (q, limit = 50) => __awaiter(this, void 0, void 0, function* () {
                const result = yield Axios.get(this.Stations.path, { params: { q, limit } });
                return result.error ? result : result.data;
            }),
            findById: (id) => __awaiter(this, void 0, void 0, function* () {
                const result = yield Axios.get(`${this.Stations.path}/${id}`);
                return result.error ? result : result.data;
            }),
            coverageList: (q, limit = 50, skip = 0, sortBy = 'callLetters', sortOrder = 'ASC', marketId) => __awaiter(this, void 0, void 0, function* () {
                const params = { q, limit, skip, sortBy, sortOrder };
                if (marketId) {
                    params.marketId = marketId;
                }
                const result = yield Axios.get(`/admin/stations/coverage`, { params });
                if (result.error)
                    return result;
                // Handle both old format (array) and new format (object with data property)
                if (result.data && typeof result.data === 'object' && 'data' in result.data && Array.isArray(result.data.data)) {
                    return result.data.data; // New paginated format
                }
                // Old format - return array directly
                if (Array.isArray(result.data)) {
                    return result.data;
                }
                // Fallback: if data exists but isn't array or paginated format, return empty array
                return [];
            }),
            coverageById: (coverageId) => __awaiter(this, void 0, void 0, function* () {
                const result = yield Axios.get(`/admin/stations/coverage/${coverageId}`);
                return result.error ? result : result.data;
            }),
            coverageGeoJSON: (coverageId) => __awaiter(this, void 0, void 0, function* () {
                const result = yield Axios.get(`/admin/stations/coverage/${coverageId}/geojson`);
                return result.error ? result : result.data;
            }),
            ingest: (payload) => __awaiter(this, void 0, void 0, function* () {
                const result = yield Axios.post(`/admin/stations/ingest`, payload);
                return result.error ? result : result.data;
            }),
            ingestFile: (payload) => __awaiter(this, void 0, void 0, function* () {
                const result = yield Axios.post(`/admin/stations/ingest-file`, payload);
                return result.error ? result : result.data;
            })
        };
        this.Presentations = {
            path: '/presentations',
            generate: (proposalInput) => __awaiter(this, void 0, void 0, function* () {
                const result = yield Axios.post('/presentations/generate', proposalInput);
                return result.error ? result : result.data;
            }),
            find: (filter, signal) => __awaiter(this, void 0, void 0, function* () {
                const filterStr = filter ? `?filter=${encodeURIComponent(JSON.stringify(filter))}` : '';
                const result = yield Axios.get(this.Presentations.path + filterStr, { signal });
                return result.error ? result : result.data;
            }),
            findById: (id, filter, signal) => __awaiter(this, void 0, void 0, function* () {
                const filterStr = filter ? `?filter=${encodeURIComponent(JSON.stringify(filter))}` : '';
                const result = yield Axios.get(this.Presentations.path + `/${id}` + filterStr, { signal });
                return result.error ? result : result.data;
            }),
            create: (data, signal) => __awaiter(this, void 0, void 0, function* () {
                const result = yield Axios.post(this.Presentations.path, data, { signal });
                return result.error ? result : result.data;
            }),
            update: (id, data, signal) => __awaiter(this, void 0, void 0, function* () {
                const result = yield Axios.patch(this.Presentations.path + `/${id}`, data, { signal });
                return result.error ? result : result.data;
            }),
            delete: (id, signal) => __awaiter(this, void 0, void 0, function* () {
                const result = yield Axios.delete(this.Presentations.path + `/${id}`, { signal });
                return result.error ? result : result.data;
            }),
            addSlide: (presentationId, slideData, signal) => __awaiter(this, void 0, void 0, function* () {
                const result = yield Axios.post(this.Presentations.path + `/${presentationId}/slides`, slideData, { signal });
                return result.error ? result : result.data;
            }),
            updateSlide: (presentationId, slideId, slideData, signal) => __awaiter(this, void 0, void 0, function* () {
                const result = yield Axios.patch(this.Presentations.path + `/${presentationId}/slides/${slideId}`, slideData, { signal });
                return result.error ? result : result.data;
            }),
            deleteSlide: (presentationId, slideId, signal) => __awaiter(this, void 0, void 0, function* () {
                const result = yield Axios.delete(this.Presentations.path + `/${presentationId}/slides/${slideId}`, { signal });
                return result.error ? result : result.data;
            }),
            reorderSlides: (presentationId, slideIds, signal) => __awaiter(this, void 0, void 0, function* () {
                const result = yield Axios.patch(this.Presentations.path + `/${presentationId}/slides/reorder`, { slideIds }, { signal });
                return result.error ? result : result.data;
            }),
            addBlock: (presentationId, slideId, blockData, signal) => __awaiter(this, void 0, void 0, function* () {
                const result = yield Axios.post(this.Presentations.path + `/${presentationId}/slides/${slideId}/blocks`, blockData, { signal });
                return result.error ? result : result.data;
            }),
            updateBlock: (presentationId, blockId, blockData, signal) => __awaiter(this, void 0, void 0, function* () {
                const result = yield Axios.patch(this.Presentations.path + `/${presentationId}/blocks/${blockId}`, blockData, { signal });
                return result.error ? result : result.data;
            }),
            deleteBlock: (presentationId, blockId, signal) => __awaiter(this, void 0, void 0, function* () {
                const result = yield Axios.delete(this.Presentations.path + `/${presentationId}/blocks/${blockId}`, { signal });
                return result.error ? result : result.data;
            }),
            searchImages: (query, perPage, signal) => __awaiter(this, void 0, void 0, function* () {
                const result = yield Axios.get(this.Presentations.path + `/search-images`, {
                    params: { query, perPage },
                    signal
                });
                return result.error ? result : (result.data.images || result.data);
            }),
            exportPdf: (id, signal) => __awaiter(this, void 0, void 0, function* () {
                const result = yield Axios.get(this.Presentations.path + `/${id}/export-pdf`, { signal });
                return result.error ? result : result.data;
            }),
            checkVideoStatus: (videoId, signal) => __awaiter(this, void 0, void 0, function* () {
                const result = yield Axios.get(this.Presentations.path + `/video-status/${videoId}`, { signal });
                return result.error ? result : result.data;
            }),
            getPreferences: (signal) => __awaiter(this, void 0, void 0, function* () {
                const result = yield Axios.get(this.Presentations.path + `/preferences`, { signal });
                return result.error ? result : result.data;
            }),
            savePreferences: (preferences, signal) => __awaiter(this, void 0, void 0, function* () {
                const result = yield Axios.post(this.Presentations.path + `/preferences`, { preferences }, { signal });
                return result.error ? result : result.data;
            }),
            adminList: (params, signal) => __awaiter(this, void 0, void 0, function* () {
                const result = yield Axios.get(`/admin/presentations`, { params: params || {}, signal });
                return result.error ? result : result.data;
            }),
            adminFindById: (source, id, signal) => __awaiter(this, void 0, void 0, function* () {
                const result = yield Axios.get(`/admin/presentations/${source}/${id}`, { signal });
                return result.error ? result : result.data;
            })
        };
        this.endpointUrl = endpointUrl || "http://localhost:8080";
        this.authToken = authToken;
        this.site = site;
        // Configure Axios defaults
        Axios.defaults.baseURL = this.endpointUrl;
        if (authToken) {
            Axios.defaults.headers.common['Authorization'] = 'Bearer ' + authToken;
        }
        Axios.defaults.headers.common['Content-Type'] = 'application/json';
        Axios.defaults.headers.common['Site'] = site;
        Axios.defaults.headers.common['source'] = site;
        // Add axios interceptor for error handling
        Axios.interceptors.response.use(response => {
            return response;
        }, error => {
            var _a, _b, _c, _d, _e, _f;
            // The error object will now contain a consistent structure
            const errorResponse = {
                error: {
                    message: ((_c = (_b = (_a = error.response) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.error) === null || _c === void 0 ? void 0 : _c.message) || error.message || 'An unexpected error occurred',
                    statusCode: ((_d = error.response) === null || _d === void 0 ? void 0 : _d.status) || 500,
                    originalError: ((_f = (_e = error.response) === null || _e === void 0 ? void 0 : _e.data) === null || _f === void 0 ? void 0 : _f.error) || error
                }
            };
            return Promise.resolve(errorResponse); // Resolve with our formatted error so component can handle it
        });
        if (localStorage.getItem('site') !== null) {
            const ls = localStorage.getItem('site');
            Axios.defaults.headers.common['Site'] = ls;
            Axios.defaults.headers.common['source'] = ls;
            this.site = ls;
        }
    }
    ChangeSite(site) {
        Axios.defaults.headers.common['Site'] = site;
        Axios.defaults.headers.common['source'] = site;
        localStorage.setItem('site', site);
        this.site = site;
    }
}
export default BackendAPIClient;
